/**
 * Pierre, feuille ou ciseaux à partir des 21 points MediaPipe Hand Landmarker — PUR.
 * Un doigt est tendu si son bout est nettement plus loin du poignet que son articulation
 * du milieu (PIP) : insensible à l'orientation de la main et à sa distance à la caméra.
 * Le pouce est ignoré (trop variable d'une personne à l'autre).
 */
import type { Point3 } from '../detection/types';

export type Throw = 'rock' | 'paper' | 'scissors';

const WRIST = 0;
const MIDDLE_MCP = 9;
/** Articulation du milieu (PIP) et bout de l'index, du majeur, de l'annulaire, de l'auriculaire. */
const FINGERS = [
  [6, 8],
  [10, 12],
  [14, 16],
  [18, 20],
] as const;
/** Bout / PIP (distances au poignet) au-delà duquel le doigt est tendu. */
export const EXTENDED_RATIO = 1.1;
const MIN_HAND_SIZE = 0.02;

const dist = (a: Point3, b: Point3): number => Math.hypot(a.x - b.x, a.y - b.y);
const valid = (p: Point3 | undefined): p is Point3 =>
  p !== undefined && Number.isFinite(p.x) && Number.isFinite(p.y);

type Fingers = readonly [boolean, boolean, boolean, boolean];

/** Doigts tendus (index, majeur, annulaire, auriculaire) ; `null` si la main est inexploitable. */
export function extendedFingers(landmarks: readonly Point3[]): Fingers | null {
  if (landmarks.length < 21 || !landmarks.every(valid)) return null;
  const wrist = landmarks[WRIST] as Point3;
  if (dist(wrist, landmarks[MIDDLE_MCP] as Point3) < MIN_HAND_SIZE) return null;
  const [a, b, c, d] = FINGERS.map(
    ([pip, tip]) =>
      dist(wrist, landmarks[tip] as Point3) >
      dist(wrist, landmarks[pip] as Point3) * EXTENDED_RATIO,
  );
  return [a, b, c, d] as [boolean, boolean, boolean, boolean];
}

const same = (a: Fingers, b: Fingers): boolean => a.every((v, i) => v === b[i]);
const SHAPES: readonly (readonly [Throw, Fingers])[] = [
  ['rock', [false, false, false, false]],
  ['paper', [true, true, true, true]],
  ['scissors', [true, true, false, false]],
];

/** Geste lu sur une image ; `null` : pas de main, ou ni pierre, ni feuille, ni ciseaux. */
export function classifyHand(landmarks: readonly Point3[] | null): Throw | null {
  const fingers = landmarks && extendedFingers(landmarks);
  if (!fingers) return null;
  return SHAPES.find(([, shape]) => same(shape, fingers))?.[0] ?? null;
}

export interface ThrowSample {
  readonly t: number;
  readonly gesture: Throw | null;
}

/** Fenêtre (avant le verrouillage) où le geste est lu : le « MI » et ce qui le précède de peu. */
export const LOCK_WINDOW_MS = 450;
const MIN_VOTES = 2;

/** Geste majoritaire de la fenêtre ; à égalité, le plus récent. `null` : rien de lisible. */
export function lockThrow(samples: readonly ThrowSample[], now: number): Throw | null {
  const votes = new Map<Throw, { count: number; last: number }>();
  for (const { t, gesture } of samples) {
    if (!gesture || t < now - LOCK_WINDOW_MS || t > now) continue;
    const v = votes.get(gesture) ?? { count: 0, last: -Infinity };
    votes.set(gesture, { count: v.count + 1, last: Math.max(v.last, t) });
  }
  let best: [Throw, { count: number; last: number }] | null = null;
  for (const entry of votes) {
    const [, v] = entry;
    if (!best || v.count > best[1].count || (v.count === best[1].count && v.last > best[1].last))
      best = entry;
  }
  return best && best[1].count >= MIN_VOTES ? best[0] : null;
}

export interface ThrowBuffer {
  /** Classe l'image et la garde ; renvoie le geste lu (pour l'indicateur). */
  push(t: number, landmarks: readonly Point3[] | null): Throw | null;
  lock(now: number): Throw | null;
  size(): number;
  reset(): void;
}

/** Garde environ la dernière seconde d'images classées. */
export function createThrowBuffer(keepMs = 2 * LOCK_WINDOW_MS): ThrowBuffer {
  let samples: ThrowSample[] = [];
  return {
    push(t, landmarks) {
      const gesture = classifyHand(landmarks);
      samples.push({ t, gesture });
      samples = samples.filter((s) => s.t >= t - keepMs);
      return gesture;
    },
    lock: (now) => lockThrow(samples, now),
    size: () => samples.length,
    reset() {
      samples = [];
    },
  };
}
